import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api/endpoints';
import type { Role, Staff } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../lib/format';
import { useToast } from '../components/Toast';
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Input,
  Modal,
  Select,
  Spinner,
} from '../components/ui';

function NewStaffDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('staff');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.auth.createStaff({
        name: name.trim(),
        username: username.trim().toLowerCase(),
        password,
        role,
      });
      toast.success(`${name} can now sign in.`);
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New staff account" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Input
          label="Full name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          autoFocus
        />
        <Input
          label="Username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoCapitalize="none"
          hint="Letters and numbers only, at least 3 characters."
          required
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          hint="At least 6 characters."
          required
        />
        <Select
          label="Role"
          value={role}
          onChange={(event) => setRole(event.target.value as Role)}
        >
          <option value="staff">Staff — the counter</option>
          <option value="admin">Admin — also stations and staff accounts</option>
        </Select>
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </Button>
      </form>
    </Modal>
  );
}

export function StaffAdminPage() {
  const toast = useToast();
  const { staff: me } = useAuth();
  const [rows, setRows] = useState<Staff[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await api.auth.listStaff());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setActive = async (row: Staff, isActive: boolean) => {
    try {
      await api.auth.setStaffStatus(row.staff_id, isActive);
      toast.success(
        isActive ? `${row.name} can sign in again.` : `${row.name} can no longer sign in.`,
      );
      load();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Staff accounts</h1>
        <Button onClick={() => setCreating(true)}>New account</Button>
      </div>

      <p className="text-sm text-slate-500">
        Accounts are deactivated, never deleted — every recharge and session they
        handled still points at them. Deactivating takes effect immediately, even
        mid-shift.
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}

      {!rows ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const isMe = row.staff_id === me?.staff_id;
            return (
              <Card key={row.staff_id}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-100">
                      {row.name}
                      {isMe && <span className="ml-2 text-xs text-sky-400">you</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      @{row.username} · added {formatDate(row.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge tone={row.role === 'admin' ? 'sky' : 'slate'}>
                      {row.role}
                    </Badge>
                    <Badge tone={row.is_active ? 'green' : 'red'}>
                      {row.is_active ? 'active' : 'deactivated'}
                    </Badge>
                    {row.is_active ? (
                      <Button
                        variant="ghost"
                        // The backend refuses this anyway; disabling it keeps
                        // staff from finding out the hard way.
                        disabled={isMe}
                        title={isMe ? 'You cannot deactivate your own account.' : undefined}
                        onClick={() => setActive(row, false)}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button variant="ghost" onClick={() => setActive(row, true)}>
                        Reactivate
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {creating && (
        <NewStaffDialog onClose={() => setCreating(false)} onSaved={load} />
      )}
    </div>
  );
}
